// src/pages/InvitationsPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  StarIcon,
  TagIcon,
  MusicalNoteIcon,
  UserGroupIcon,
  TicketIcon,
} from "@heroicons/react/24/solid";
import EventLayout from "../components/EventLayout";
import MailSettingsModal from "../components/MailSettingsModal";
import AddInvitationModal from "../components/AddInvitationModal";
import ConfirmModal from "../components/ConfirmModal";
import EditInvitationModal from "../components/EditInvitationModal";
import AddFromPermanentInvitationModal from "../components/AddFromPermanentInvitationModal";
import Spinner from "../components/Spinner";
import { toast } from "react-toastify";
import { SERVER_BASE_URL } from "../config";

const SEND_COOLDOWN_MS = 60000;

function InvitationsPage() {
  const { id } = useParams();

  // Функция для отображения иконки категории гостя
  const renderCategoryIcon = (category) => {
    switch (category) {
      case "VIP":
        return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
      case "BACKSTAGE":
        return <TagIcon className="h-4 w-4 text-purple-500" title="Backstage" />;
      case "ARTIST":
        return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
      case "STAFF":
        return <UserGroupIcon className="h-4 w-4 text-purple-500" title="Staff" />;
      case "REGULAR":
      default:
        return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
    }
  };

  // -----------------------------
  //  Состояния
  // -----------------------------
  const [invitations, setInvitations] = useState([]);
  const [showMailSettingsModal, setShowMailSettingsModal] = useState(false);
  const [showAddInvitationModal, setShowAddInvitationModal] = useState(false);
  const [sortBy, setSortBy] = useState("invitee_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [invitationToEdit, setInvitationToEdit] = useState(null);
  const [mailSettings, setMailSettings] = useState({
    fromAddress: "",
    replyTo: "",
    subject: "",
    template: "",
  });
  const [showAddFromInvitationListModal, setShowAddFromInvitationListModal] = useState(false);
  const [permanentInvitationListsOptions, setPermanentInvitationListsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sendingInvitationIds, setSendingInvitationIds] = useState([]);
  const [sendCooldownUntil, setSendCooldownUntil] = useState({});
  // Новые состояния для выделения множества приглашений
  const [selectedInvitationIds, setSelectedInvitationIds] = useState([]);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);

  // -----------------------------
  //  Текущий пользователь (для проверки organization)
  // -----------------------------
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);

  // -----------------------------
  //  Функция для определения, мобильное ли устройство
  // -----------------------------
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // -----------------------------
  //  Запрос: Получить текущего пользователя
  // -----------------------------
  const fetchCurrentUser = useCallback(async () => {
    try {
      setIsUserLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error("Failed to fetch current user");
      }
      const data = await res.json();
      setCurrentUser(data); // У пользователя есть поле "organization"
    } catch (error) {
      console.error("Error fetching current user:", error);
      toast.error("Error fetching user data");
    } finally {
      setIsUserLoading(false);
    }
  }, []);

  // -----------------------------
  //  Запрос: Получить приглашения
  // -----------------------------
  const fetchInvitations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_BASE_URL}/api/invitations/?event_id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setInvitations(data);
      } else {
        console.error("Failed to load invitations");
        toast.error("Failed to load invitations");
      }
    } catch (error) {
      console.error("Error loading invitations:", error);
      toast.error("Error loading invitations");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // -----------------------------
  //  Запрос: Получить списки постоянных приглашений (для AddFromPermanentInvitationModal)
  // -----------------------------
  const fetchPermanentInvitationListsForModal = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${SERVER_BASE_URL}/api/permanent-invitations/lists/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Failed to load permanent invitation lists");
      }
      const data = await response.json();
      const options = data.map((list) => ({
        value: list.id,
        label: list.name,
      }));
      setPermanentInvitationListsOptions(options);
    } catch (error) {
      console.error("Error loading permanent invitation lists:", error);
      toast.error("Failed to load permanent invitation lists.");
    }
  }, []);

  // -----------------------------
  //  useEffect для начальной загрузки
  // -----------------------------
  useEffect(() => {
    fetchCurrentUser();
    fetchInvitations();
    fetchPermanentInvitationListsForModal();

    // Загружаем локальные настройки почты
    const savedSettings = JSON.parse(localStorage.getItem("mailSettings") || "{}");
    setMailSettings({
      fromAddress: savedSettings.fromAddress || "",
      replyTo: savedSettings.replyTo || "",
      subject: savedSettings.subject || "",
      template: savedSettings.template || "",
    });
  }, [fetchCurrentUser, fetchInvitations, fetchPermanentInvitationListsForModal]);

  // -----------------------------
  //  Сохранение mailSettings (из MailSettingsModal)
  // -----------------------------
  const saveMailSettings = ({ fromAddress, replyTo, subject, template }) => {
    setMailSettings({ fromAddress, replyTo, subject, template });
    localStorage.setItem(
      "mailSettings",
      JSON.stringify({ fromAddress, replyTo, subject, template })
    );
  };

  // -----------------------------
  //  Проверяем, заполнено ли поле organization
  // -----------------------------
  const isOrganizationMissing = () => {
    // Пока грузим данные пользователя — считаем, что организация отсутствует
    if (isUserLoading) return true;
    // Если currentUser нет, или organization пустая — организация не задана
    return !currentUser?.organization || currentUser.organization.trim() === "";
  };

  // -----------------------------
  //  Обработчик: Добавление приглашения
  // -----------------------------
  const handleCreated = (newInvitation) => {
    setInvitations((prev) => [...prev, newInvitation]);
  };

  // -----------------------------
  //  Обработчик: Открыть/закрыть разные модалки
  // -----------------------------
  const openMailSettingsModal = () => setShowMailSettingsModal(true);
  const closeMailSettingsModal = () => setShowMailSettingsModal(false);

  const getResponseErrorMessage = async (response, fallback) => {
    let text = "";
    try {
      text = await response.text();
    } catch {
      return fallback;
    }

    if (!text) return fallback;

    try {
      const data = JSON.parse(text);
      return data.detail || data.message || fallback;
    } catch {
      return text;
    }
  };

  const buildMailPayload = () => ({
    fromAddress: mailSettings.fromAddress,
    replyTo: mailSettings.replyTo,
    subject: mailSettings.subject,
    template: mailSettings.template,
  });

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const isInvitationSending = (invitationId) =>
    sendingInvitationIds.includes(invitationId);

  const isInvitationCoolingDown = (invitationId) =>
    (sendCooldownUntil[invitationId] || 0) > Date.now();

  const isInvitationSendBlocked = (invitationId) =>
    isInvitationSending(invitationId) || isInvitationCoolingDown(invitationId);

  const startInvitationCooldown = (invitationId) => {
    setSendCooldownUntil((prev) => ({
      ...prev,
      [invitationId]: Date.now() + SEND_COOLDOWN_MS,
    }));

    window.setTimeout(() => {
      setSendCooldownUntil((prev) => {
        const next = { ...prev };
        delete next[invitationId];
        return next;
      });
    }, SEND_COOLDOWN_MS);
  };

  const getSendButtonTitle = (invitation) => {
    if (isOrganizationMissing()) {
      return "Please set your organization name before sending";
    }
    if (isInvitationSending(invitation.id)) {
      return "Sending invitation";
    }
    if (isInvitationCoolingDown(invitation.id)) {
      return "Please wait before resending";
    }
    return invitation.status === "sent" ? "Resend invitation" : "Send invitation";
  };

  const openAddInvitationModal = () => setShowAddInvitationModal(true);
  const closeAddInvitationModal = () => setShowAddInvitationModal(false);

  // -----------------------------
  //  ConfirmModal (удаление приглашения)
  // -----------------------------
  const openConfirmModal = (invitation) => {
    setInvitationToDelete(invitation);
    setShowConfirmModal(true);
  };
  const closeConfirmModal = () => {
    setInvitationToDelete(null);
    setShowConfirmModal(false);
  };

  // -----------------------------
  //  Удаление приглашения
  // -----------------------------
  const handleDeleteInvitation = async (invitationId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_BASE_URL}/api/invitations/${invitationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
        // Удаляем приглашение из выбранных, если оно там есть
        setSelectedInvitationIds((prev) => prev.filter((id) => id !== invitationId));
        toast.success("Invitation deleted successfully");
      } else {
        console.error("Failed to delete invitation");
        toast.error("Failed to delete invitation");
      }
    } catch (error) {
      console.error("Error deleting invitation:", error);
      toast.error("Error deleting invitation");
    }
  };

  const confirmDeleteInvitation = async () => {
    if (!invitationToDelete) return;
    await handleDeleteInvitation(invitationToDelete.id);
    closeConfirmModal();
  };

  // -----------------------------
  //  Редактирование приглашения
  // -----------------------------
  const openEditModal = (invitation) => {
    setInvitationToEdit(invitation);
    setShowEditModal(true);
  };
  const closeEditModal = () => {
    setInvitationToEdit(null);
    setShowEditModal(false);
  };
  const handleUpdated = (updatedInvitation) => {
    setInvitations((prev) =>
      prev.map((inv) => (inv.id === updatedInvitation.id ? updatedInvitation : inv))
    );
  };

  // -----------------------------
  //  Сортировка приглашений
  // -----------------------------
  const handleSort = (criteria) => {
    if (criteria === sortBy) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(criteria);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortBy) {
      return <span className="text-gray-400">–</span>;
    }
    return sortOrder === "asc" ? (
      <span className="text-gray-600" style={{ fontSize: "11px" }}>▲</span>
    ) : (
      <span className="text-gray-600" style={{ fontSize: "11px" }}>▼</span>
    );
  };

  // -----------------------------
  //  Фильтрация + сортировка
  // -----------------------------
  const sortedInvitations = [...invitations].sort((a, b) => {
    const fieldA = (a[sortBy] || "").toString().toLowerCase();
    const fieldB = (b[sortBy] || "").toString().toLowerCase();
    return sortOrder === "asc" ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
  });

  const filteredInvitations = sortedInvitations.filter((invitation) =>
    invitation.invitee_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    invitation.invitee_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // -----------------------------
  //  Подсчёт статистики
  // -----------------------------
  const calculateInvitationStats = () => {
    const totalInvitations = invitations.length;
    const sentInvitations = invitations.filter((inv) => inv.status === "sent").length;
    const unsentInvitations = totalInvitations - sentInvitations;

    const uniqueVariants = new Set();
    invitations.forEach((invitation) => {
      invitation.variants.forEach((variant) => {
        if (variant.limit > 0) {
          uniqueVariants.add(variant.type);
        }
      });
    });

    return {
      totalInvitations,
      sentInvitations,
      unsentInvitations,
      uniqueVariantTypes: uniqueVariants.size,
    };
  };

  const stats = calculateInvitationStats();

  // -----------------------------
  //  Импорт приглашений из постоянных списков
  // -----------------------------
  const handleSaveInvitationForImport = async (invitationData) => {
    try {
      const token = localStorage.getItem("token");
      const payload = {
        event_id: invitationData.event_id,
        invitee_name: invitationData.invitee_name,
        invitee_email: invitationData.invitee_email,
        variants: invitationData.variants || [],
      };

      const response = await fetch(`${SERVER_BASE_URL}/api/invitations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create invitation during import");
      }
    } catch (error) {
      console.error("Failed to save invitation during import:", error);
      toast.error("Failed to save invitation during import.");
    }
  };

  const handleAddInvitationsFromList = async (selectedListIds) => {
    if (selectedListIds.length === 0) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      for (const listId of selectedListIds) {
        const response = await fetch(
          `${SERVER_BASE_URL}/api/permanent-invitations/lists/${listId}/invitations/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch invitations from list ${listId}`);
        }

        const data = await response.json();
        for (const invitationFromList of data) {
          const invitationDataForEvent = {
            event_id: Number(id),
            invitee_name: invitationFromList.invitee_name,
            invitee_email: invitationFromList.invitee_email,
            variants: invitationFromList.variants || [],
          };

          const duplicate = invitations.find(
            (inv) =>
              inv.event_id === Number(id) &&
              inv.invitee_email.toLowerCase() === invitationDataForEvent.invitee_email.toLowerCase() &&
              ((inv.invitee_name || "").toLowerCase() ===
                (invitationDataForEvent.invitee_name || "").toLowerCase())
          );

          if (duplicate) {
            console.log(`Skipping duplicate invitation for email ${invitationDataForEvent.invitee_email}`);
            toast.info(`Invitation for ${invitationDataForEvent.invitee_email} already exists. Skipping.`);
            continue;
          }

          await handleSaveInvitationForImport(invitationDataForEvent);
        }
      }
      await fetchInvitations();
      toast.success("Invitations from selected lists added successfully.");
      setShowAddFromInvitationListModal(false);
    } catch (error) {
      console.error("Error adding invitations from lists:", error);
      toast.error("Failed to add invitations from selected lists.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  //  Отправка всех писем
  // -----------------------------
  const handleSendAllEmails = async () => {
    if (loading || bulkSending) {
      return;
    }
    if (isOrganizationMissing()) {
      toast.error("Please set your organization name before sending emails!");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const unsentInvitations = invitations.filter((inv) => inv.status !== "sent");
      if (unsentInvitations.length === 0) {
        toast.info("All emails have already been sent.");
        return;
      }
      setLoading(true);
      setBulkSending(true);

      for (const invitation of unsentInvitations) {
        const response = await fetch(
          `${SERVER_BASE_URL}/api/invitations/${invitation.id}/send`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(buildMailPayload()),
          }
        );
        if (!response.ok) {
          const message = await getResponseErrorMessage(
            response,
            `Failed to send email for invitation ${invitation.id}`
          );
          throw new Error(message);
        }
        await response.json();
        startInvitationCooldown(invitation.id);
        await delay(1500);
      }

      toast.success("All emails sent successfully!");
      await fetchInvitations();
    } catch (error) {
      console.error("Error sending all emails:", error);
      toast.error(error.message || "Error sending all emails");
    } finally {
      setBulkSending(false);
      setLoading(false);
    }
  };

  // -----------------------------
  //  Отправка письма по одному
  // -----------------------------
  const handleSendEmail = async (invitationId) => {
    if (isOrganizationMissing()) {
      toast.error("Please set your organization name before sending emails!");
      return;
    }
    if (isInvitationSendBlocked(invitationId)) {
      return;
    }

    try {
      setSendingInvitationIds((prev) =>
        prev.includes(invitationId) ? prev : [...prev, invitationId]
      );

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${SERVER_BASE_URL}/api/invitations/${invitationId}/send`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(buildMailPayload()),
        }
      );

      if (!response.ok) {
        const message = await getResponseErrorMessage(response, "Failed to send email");
        throw new Error(message);
      }

      const result = await response.json();
      toast.success(result.detail || "Email sent successfully!");
      startInvitationCooldown(invitationId);
      fetchInvitations();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Error sending email");
    } finally {
      setSendingInvitationIds((prev) => prev.filter((id) => id !== invitationId));
    }
  };

  // -----------------------------
  //  Цвет статуса
  // -----------------------------
  const getStatusColor = (status) => {
    return status === "sent" ? "bg-green-500" : "bg-orange-500";
  };

  // -----------------------------
  //  Новые функции для работы с чекбоксами
  // -----------------------------
  const handleCheckboxChange = (invitationId) => {
    setSelectedInvitationIds((prev) =>
      prev.includes(invitationId)
        ? prev.filter((id) => id !== invitationId)
        : [...prev, invitationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedInvitationIds.length === filteredInvitations.length) {
      setSelectedInvitationIds([]);
    } else {
      setSelectedInvitationIds(filteredInvitations.map((inv) => inv.id));
    }
  };

  const handleDeleteSelectedInvitations = async () => {
    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        selectedInvitationIds.map((id) =>
          fetch(`${SERVER_BASE_URL}/api/invitations/${id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        )
      );
      setInvitations((prev) =>
        prev.filter((inv) => !selectedInvitationIds.includes(inv.id))
      );
      setSelectedInvitationIds([]);
      setShowBulkConfirmModal(false);
      toast.success("Selected invitations deleted successfully");
    } catch (error) {
      console.error("Error deleting selected invitations:", error);
      toast.error("Error deleting selected invitations");
    }
  };

  // -----------------------------
  //  Рендер
  // -----------------------------
  return (
    <EventLayout hideDescription>
      <div
        className="container mx-auto max-w-5xl px-0 sm:px-4 py-4"
        style={{ fontSize: isMobile() ? "0.9rem" : "1rem" }}
      >
        {/* Заголовок + кнопки */}
        <div className="bg-white rounded-none sm:rounded-lg shadow-md p-3 sm:p-6 mb-4 sm:mb-6 mx-0">
          <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center">
            <div className="flex flex-row gap-2 mb-2 sm:mb-0 w-full sm:w-auto">
              <button
                onClick={openAddInvitationModal}
                className="w-full sm:w-auto flex items-center justify-center bg-indigo-600 text-white font-bold px-3 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
              >
                <PlusIcon className="h-4 w-4 sm:mr-2" />
                <span className="sm:inline hidden">Add</span> Invitation
              </button>

              <button
                onClick={() => setShowAddFromInvitationListModal(true)}
                className="w-full sm:w-auto flex items-center justify-center bg-indigo-600 text-white font-bold px-3 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
              >
                <PlusIcon className="h-4 w-4 sm:mr-2" />
                <span className="sm:inline hidden">Add from</span> List
              </button>
              
              {/* Кнопка удаления выбранных приглашений */}
              {selectedInvitationIds.length > 0 && (
                <button
                  onClick={() => setShowBulkConfirmModal(true)}
                  className="w-full sm:w-auto flex items-center justify-center bg-white text-gray-600 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
                >
                  <TrashIcon className="h-4 w-4 sm:mr-2" />
                  {!isMobile() && ""} ({selectedInvitationIds.length})
                </button>
              )}
            </div>

            <div className="w-full sm:flex-1 flex sm:ml-2 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Search invitations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <button
              onClick={openMailSettingsModal}
              className="hidden sm:flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
            >
              <Cog6ToothIcon className="h-4 w-4 mr-2" />
              <span>Mail Settings</span>
            </button>
          </div>

          {/* Подсказка, если организация не задана */}
          {isOrganizationMissing() && (
            <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 text-sm rounded">
              Organization name is not set. Please update it in your Settings before sending invitations!
            </div>
          )}

          {/* Статистика */}
          <div className="hidden sm:grid sm:grid-cols-4 sm:gap-2 mb-4">
            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Total</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{stats.totalInvitations}</span>
              </p>
            </div>

            <div className="py-2 px-3 rounded-lg bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Sent</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{stats.sentInvitations}</span>
              </p>
            </div>

            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Unsent</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{stats.unsentInvitations}</span>
              </p>
            </div>

            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Variants</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{stats.uniqueVariantTypes}</span>
              </p>
            </div>
          </div>

          {/* Мобильная версия статистики */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Total</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {stats.totalInvitations}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Sent</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {stats.sentInvitations}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Unsent</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {stats.unsentInvitations}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Variants</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {stats.uniqueVariantTypes}
              </p>
            </div>
          </div>

          {/* Кнопка Send All Unsent */}
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={handleSendAllEmails}
                disabled={isOrganizationMissing() || loading || bulkSending}
                className={`flex items-center bg-indigo-600 text-white font-bold px-4 py-2 rounded hover:bg-indigo-700 transition duration-200 text-sm ${
                  isOrganizationMissing() || loading || bulkSending
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                {bulkSending ? "Sending..." : isMobile() ? "Send All" : "Send All Unsent"}
              </button>
            </div>

            {/* Кнопка Mail Settings для мобильного */}
            <button
              onClick={openMailSettingsModal}
              className="sm:hidden flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
            >
              <Cog6ToothIcon className="h-4 w-4 mr-2" />
              <span>{isMobile() ? "Settings" : "Mail Settings"}</span>
            </button>
          </div>
        </div>

        {/* Invitations table (desktop) */}
        <div className="hidden sm:block bg-white rounded-lg shadow-md overflow-hidden relative mx-0">
          <div className="grid grid-cols-[40px_40px_1fr_1fr_1fr_80px] gap-1 bg-gray-50 p-3 border-b border-gray-200">
            <div className="flex justify-center items-center">
              <input
                type="checkbox"
                checked={selectedInvitationIds.length === filteredInvitations.length && filteredInvitations.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            </div>
            <div
              className="text-sm font-medium cursor-pointer flex items-center justify-center"
              onClick={() => handleSort("status")}
            >
              <span>Status</span>
              <span className="ml-1">{renderSortIcon("status")}</span>
            </div>
            <div
              className="text-sm font-medium cursor-pointer flex items-center"
              onClick={() => handleSort("invitee_name")}
            >
              <span>Name</span>
              <span className="ml-1">{renderSortIcon("invitee_name")}</span>
            </div>
            <div
              className="text-sm font-medium cursor-pointer flex items-center"
              onClick={() => handleSort("invitee_email")}
            >
              <span>Email</span>
              <span className="ml-1">{renderSortIcon("invitee_email")}</span>
            </div>
            <div className="text-sm font-medium">Variants</div>
            <div className="text-sm font-medium text-center">Actions</div>
          </div>

          {filteredInvitations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No invitations found{searchQuery ? " matching your search" : ""}.</p>
            </div>
          ) : (
            <div>
              {filteredInvitations.map((invitation, index) => (
                <div
                  key={invitation.id}
                  className={`grid grid-cols-[40px_40px_1fr_1fr_1fr_80px] items-center border-b border-gray-200 p-3 ${
                    index % 2 === 0 ? "" : "bg-gray-50"
                  } hover:bg-indigo-50 transition duration-200`}
                >
                  <div className="flex justify-center items-center">
                    <input
                      type="checkbox"
                      checked={selectedInvitationIds.includes(invitation.id)}
                      onChange={() => handleCheckboxChange(invitation.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="flex justify-center items-center">
                    <div
                      className={`rounded-full w-3 h-3 ${getStatusColor(invitation.status)}`}
                      title={invitation.status === "sent" ? "Sent" : "Not sent"}
                    />
                  </div>
                  <div className="truncate font-medium flex items-center gap-1">
                    {invitation.guest_category && 
                      renderCategoryIcon(invitation.guest_category)
                    }
                    {invitation.invitee_name}
                </div>
                  <div className="truncate text-gray-600">{invitation.invitee_email}</div>
                  <div className="flex flex-wrap gap-1">
                    {invitation.variants
                      .filter((variant) => variant.limit > 0)
                      .map((variant, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs border border-gray-300 flex items-center"
                        >
                          <span className="mr-1">
                            {renderCategoryIcon(variant.category || "REGULAR")}
                          </span>
                          {variant.type} x {variant.limit}
                        </span>
                      ))}
                  </div>
                  <div className="flex justify-center space-x-2">
                    <button
                      onClick={() => handleSendEmail(invitation.id)}
                      disabled={isOrganizationMissing() || isInvitationSendBlocked(invitation.id)}
                      className={`text-indigo-600 hover:text-indigo-800 ${
                        isOrganizationMissing() || isInvitationSendBlocked(invitation.id)
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      title={getSendButtonTitle(invitation)}
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(invitation)}
                      className="text-gray-500 hover:text-yellow-700"
                      title="Edit invitation"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openConfirmModal(invitation)}
                      className="text-gray-500 hover:text-red-600"
                      title="Delete invitation"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
              <Spinner />
            </div>
          )}
        </div>

        {/* Invitations table (mobile) */}
        <div className="sm:hidden block bg-white rounded-none sm:rounded-lg shadow-md overflow-hidden relative mt-4 mx-0">
          {/* Mobile header with select all */}
          <div className="flex justify-between items-center py-1.5 px-3 border-b border-gray-200 bg-gray-50">
            <div className="text-sm font-medium">Invitations</div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="select-all-mobile"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                checked={selectedInvitationIds.length === filteredInvitations.length && filteredInvitations.length > 0}
                onChange={handleSelectAll}
              />
              <label htmlFor="select-all-mobile" className="ml-1 text-xs text-gray-600">All</label>
            </div>
          </div>
          
          {filteredInvitations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <p>No invitations found{searchQuery ? " matching your search" : ""}.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredInvitations.map((invitation) => (
                <div key={invitation.id} className="bg-white p-3">
                  <div className="flex items-center mb-1">
                    <input
                      type="checkbox"
                      checked={selectedInvitationIds.includes(invitation.id)}
                      onChange={() => handleCheckboxChange(invitation.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div
                      className={`rounded-full w-2.5 h-2.5 ${getStatusColor(invitation.status)} mr-2 flex-shrink-0`}
                    />
                    <div className="font-medium truncate mr-2 flex items-center gap-1">
                      {invitation.guest_category && 
                        renderCategoryIcon(invitation.guest_category)
                      }
                      {invitation.invitee_name}
                  </div>
                    <div className="flex flex-wrap gap-1 ml-auto">
                      {invitation.variants
                        .filter((variant) => variant.limit > 0)
                        .map((variant, idx) => (
                          <span
                            key={idx}
                            className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full text-xs border border-gray-300 whitespace-nowrap flex items-center"
                          >
                            <span className="mr-1">
                              {renderCategoryIcon(variant.category || "REGULAR")}
                            </span>
                            {variant.type} x {variant.limit}
                          </span>
                        ))}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-gray-600 text-sm truncate flex-grow">{invitation.invitee_email}</div>
                    <div className="flex space-x-3 ml-2 flex-shrink-0">
                      <button
                        onClick={() => handleSendEmail(invitation.id)}
                        disabled={isOrganizationMissing() || isInvitationSendBlocked(invitation.id)}
                        className={`text-indigo-600 hover:text-indigo-800 ${
                          isOrganizationMissing() || isInvitationSendBlocked(invitation.id)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        title={getSendButtonTitle(invitation)}
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(invitation)}
                        className="text-gray-500 hover:text-yellow-700"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openConfirmModal(invitation)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
              <Spinner />
            </div>
          )}
        </div>

        {/* Модальные окна */}
        {showEditModal && invitationToEdit && (
          <EditInvitationModal
            invitation={invitationToEdit}
            onClose={closeEditModal}
            onUpdated={handleUpdated}
          />
        )}
        {showConfirmModal && (
          <ConfirmModal
            isOpen={showConfirmModal}
            onClose={closeConfirmModal}
            onConfirm={confirmDeleteInvitation}
            message="Are you sure you want to delete this invitation?"
          />
        )}
        {showBulkConfirmModal && (
          <ConfirmModal
            isOpen={showBulkConfirmModal}
            onClose={() => setShowBulkConfirmModal(false)}
            onConfirm={handleDeleteSelectedInvitations}
            message={`Are you sure you want to delete ${selectedInvitationIds.length} selected invitations?`}
          />
        )}
        {showMailSettingsModal && (
          <MailSettingsModal
            onClose={closeMailSettingsModal}
            currentFrom={mailSettings.fromAddress}
            currentReplyTo={mailSettings.replyTo}
            currentSubject={mailSettings.subject}
            currentTemplate={mailSettings.template}
            onSave={saveMailSettings}
          />
        )}
        {showAddInvitationModal && (
          <AddInvitationModal
            eventId={Number(id)}
            onClose={closeAddInvitationModal}
            onCreated={handleCreated}
          />
        )}
        {showAddFromInvitationListModal && (
          <AddFromPermanentInvitationModal
            isOpen={showAddFromInvitationListModal}
            onClose={() => setShowAddFromInvitationListModal(false)}
            permanentInvitationLists={permanentInvitationListsOptions}
            onAddInvitationsFromList={handleAddInvitationsFromList}
          />
        )}
      </div>
    </EventLayout>
  );
}

export default InvitationsPage;
